import React, { useEffect, useState } from 'react';
import {
  Typography,
  Switch,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableHead,
  TableRow,
} from '@material-ui/core';
import {
  Chart,
  ArgumentAxis,
  AreaSeries,
  LineSeries,
  Tooltip,
} from '@devexpress/dx-react-chart-bootstrap4';
import { Animation, EventTracker } from '@devexpress/dx-react-chart';
import { find, flatMap, get, isEmpty, orderBy } from 'lodash';
import moment from 'moment';
import { line, area, curveStepAfter } from 'd3-shape';
import { useSelector } from 'react-redux';

import { Box, StyledTableCell, StyledTableRow } from 'components';
import {
  errorMessageHandler,
  formatAmount,
  formatMoney,
  sortByDate,
  getNavTotal,
  getNavTotalLatestDate,
} from 'common/utils/helpers';
import {
  getInvestorAggregatePortfolio,
  getInvestorNavTrack,
  getInvestorPayments,
  getInvestorPortfolio,
} from 'services';
import history from 'common/utils/history';
import { RootState } from 'redux-modules/store/rootState';
import { Deal } from 'redux-modules/deals/Types';
import PendingInvestment from 'views/Dashboard/components/PendingInvestment';

import styles from './styles';
import PortfolioSummary from './components/PortfolioSummary';

const format = () => (tick: any) => tick;

const Area = (props: any) => (
  <AreaSeries.Path
    {...props}
    path={area()
      .x(({ arg }: any) => arg)
      .y1(({ val }: any) => val)
      .y0(({ startVal }: any) => startVal)
      .curve(curveStepAfter)}
  />
);

const Line = (props: any) => (
  <LineSeries.Path
    {...props}
    path={line()
      .x(({ arg }: any) => arg)
      .y(({ val }: any) => val)
      .curve(curveStepAfter)}
  />
);

const TooltipContent = (props: any) => (
  <Tooltip.Content {...props} text={`${formatAmount(props.text)}`} />
);

const Portfolio = () => {
  const classes = styles();
  const search = history.location.search;
  const spv: any = new URLSearchParams(search).get('spv');

  const { investorDeals } = useSelector(({ Investor }: RootState) => Investor);

  const { dealList } = useSelector(({ Deals }: RootState) => Deals);

  const [selectedSPV, setSelectedSPV] = React.useState<string>('');
  const [open, setOpen] = React.useState<boolean>(false);
  const [selectedNav, setSelectedNav] = React.useState<boolean>(false);
  const [aggregatePortfolioData, setAggregatePortfolioData] = useState<any>({});
  const [portfolioCardData, setPortfolioCardData] = useState<any>({});
  const [navTrackData, setNavTrackData] = useState<any>([]);
  const [paymentsList, setPaymentsList] = useState<any>([]);
  // eslint-disable-next-line no-unused-vars
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // eslint-disable-next-line no-unused-vars
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [chartData, setChartData] = useState<any>([]);
  const [selectedSummarySPV, setSelectedSummarySPV] = React.useState<string>(
    '',
  );
  const [targetItem, setTargetItem] = React.useState<any>();

  const getNavChartData = (
    navTrackDataV2: any,
    portfolioCardDataV2: any,
    addTodayDate = true,
  ) => {
    if (!isEmpty(navTrackDataV2)) {
      const sorted = navTrackDataV2.sort(sortByDate);
      const navValueBeforeInvest = sorted
        .slice()
        .reverse()
        .find((navTrack: any) => {
          if (
            moment(get(navTrack, 'date')).isBefore(
              get(portfolioCardDataV2, 'investmentDate'),
            )
          )
            return navTrack;
        });
      const newSortedTrack = sorted.filter((navTrack: any) => {
        if (
          !moment(get(navTrack, 'date')).isBefore(
            get(portfolioCardDataV2, 'investmentDate'),
          )
        )
          return navTrack;
      });
      const lastIndex = newSortedTrack.length - 1;
      const data = newSortedTrack.map((item: any) => {
        return {
          month: moment(get(item, 'date')).format('DD MMM YY'),
          nav: parseFloat(get(item, 'nav', 0)),
          tv:
            parseFloat(get(item, 'nav', 0)) +
            parseFloat(get(item, 'distribution', 0)),
        };
      });
      data.unshift.apply(data, [
        {
          month: moment(get(portfolioCardDataV2, 'investmentDate')).format(
            'DD MMM YY',
          ),
          nav: parseFloat(
            get(navValueBeforeInvest, 'nav') ||
              get(portfolioCardDataV2, 'totalInvestments', 0),
          ),
          tv:
            parseFloat(
              get(navValueBeforeInvest, 'nav') ||
                get(portfolioCardDataV2, 'totalInvestments', 0),
            ) + parseFloat(get(navValueBeforeInvest, 'distribution') || 0),
        },
      ]);
      addTodayDate &&
        data.push({
          month: moment().format('DD MMM YY'),
          nav: parseFloat(
            get(newSortedTrack, `[${lastIndex}].nav`) ||
              get(navValueBeforeInvest, 'nav') ||
              get(portfolioCardDataV2, 'totalAssetValue', 0),
          ),
          tv:
            parseFloat(
              get(newSortedTrack, `[${lastIndex}].nav`) ||
                get(navValueBeforeInvest, 'nav') ||
                get(portfolioCardDataV2, 'totalAssetValue', 0),
            ) +
            parseFloat(
              get(newSortedTrack, `[${lastIndex}].distribution`) ||
                get(navValueBeforeInvest, 'distribution') ||
                get(portfolioCardDataV2, 'comulativeDistribution', 0),
            ),
        });
      return data;
    } else {
      return [
        ...(addTodayDate
          ? [
              {
                month: moment(
                  get(portfolioCardDataV2, 'investmentDate'),
                ).format('DD MMM YY'),
                nav: parseFloat(
                  get(portfolioCardDataV2, 'totalInvestments', 0),
                ),
                tv: parseFloat(get(portfolioCardDataV2, 'totalInvestments', 0)),
              },
              {
                month: moment().format('DD MMM YY'),
                nav: parseFloat(get(portfolioCardDataV2, 'totalAssetValue', 0)),
                tv:
                  parseFloat(get(portfolioCardDataV2, 'totalAssetValue', 0)) +
                  parseFloat(
                    get(portfolioCardDataV2, 'comulativeDistribution', 0),
                  ),
              },
            ]
          : [
              {
                month: moment(
                  get(portfolioCardDataV2, 'investmentDate'),
                ).format('DD MMM YY'),
                nav: parseFloat(
                  get(portfolioCardDataV2, 'totalInvestments', 0),
                ),
                tv: parseFloat(get(portfolioCardDataV2, 'totalInvestments', 0)),
              },
            ]),
      ];
    }
  };

  const setNavChartData = (navTrackDataV2: any, portfolioCardDataV2: any) => {
    setChartData([]);
    const spvChartData = getNavChartData(navTrackDataV2, portfolioCardDataV2);
    setChartData(spvChartData);
  };

  const setNavChartAllData = (
    navTrackDataV2: any,
    portfolioCardDataV2: any,
  ) => {
    setChartData([]);
    const orderByportfolioCardData = orderBy(
      portfolioCardDataV2,
      'investmentDate',
      'asc',
    );
    const allSpvChartData = orderByportfolioCardData.map(
      (portfolioData: any, index: number) =>
        getNavChartData(
          navTrackDataV2.filter(
            (navTrack: any) => navTrack.spv === portfolioData.spv,
          ),
          portfolioData,
          index === orderByportfolioCardData.length - 1,
        ),
    );
    const allSpvChartNewData = JSON.parse(JSON.stringify(allSpvChartData));
    allSpvChartNewData.map((spvChartData: any, index: number) => {
      if (index) {
        const prevSpvData = allSpvChartNewData[index - 1];
        const prevSpvLastData = prevSpvData[prevSpvData.length - 1];
        return spvChartData.map((chartData: any) => {
          chartData.nav = chartData.nav + get(prevSpvLastData, 'nav', 0);
          chartData.tv = chartData.tv + get(prevSpvLastData, 'tv', 0);
          return chartData;
        });
      } else return spvChartData;
    });
    setChartData(flatMap(allSpvChartNewData));
  };

  const handleSwitchChange = (event: any) => {
    setSelectedNav(!event.target.checked);
  };

  const handleFilterClose = () => {
    setOpen(false);
  };
  const handleFilterOpen = () => {
    setOpen(true);
  };

  useEffect(() => {
    if (isEmpty(spv)) {
      setIsLoading(true);
      Promise.all([getInvestorAggregatePortfolio(), getInvestorPayments()])
        .then(([aggregateData, paymentData]: any) => {
          setAggregatePortfolioData(aggregateData);
          setPaymentsList(paymentData);
          setIsLoading(false);
        })
        .catch((err: any) => {
          const message: string = errorMessageHandler(err);
          setErrorMessage(message);
          setIsLoading(false);
          setNavTrackData([]);
        });
    }
  }, []);

  useEffect(() => {
    if (isEmpty(spv)) {
      if (!isEmpty(selectedSPV)) {
        setIsLoading(true);
        Promise.all([
          getInvestorNavTrack(selectedSPV),
          getInvestorPortfolio(selectedSPV),
        ])
          .then(([trackData, portfolioData]: any) => {
            setNavTrackData(trackData);
            setPortfolioCardData(portfolioData);
            setNavChartData(trackData, portfolioData);
            setIsLoading(false);
          })
          .catch((err: any) => {
            const message: string = errorMessageHandler(err);
            setErrorMessage(message);
            setIsLoading(false);
            setNavTrackData([]);
          });
      } else {
        setIsLoading(true);
        const portfolioPromise = investorDeals.map((spv: string) =>
          getInvestorPortfolio(spv),
        );
        Promise.all([getInvestorNavTrack(), ...portfolioPromise])
          .then(([trackData, ...portfolioPromiseData]: any) => {
            setNavTrackData(trackData);
            setPortfolioCardData(portfolioPromiseData);
            setNavChartAllData(trackData, portfolioPromiseData);
            setIsLoading(false);
          })
          .catch((err: any) => {
            const message: string = errorMessageHandler(err);
            setErrorMessage(message);
            setIsLoading(false);
            setNavTrackData([]);
          });
      }
    }
  }, [selectedSPV, investorDeals]);

  useEffect(() => {
    if (!isEmpty(spv) && isEmpty(selectedSummarySPV) && !isEmpty(dealList))
      setSelectedSummarySPV(get(find(dealList, { deal: spv }), 'id') || '');
  }, [spv, dealList]);

  return (
    <>
      {!isEmpty(spv) ? (
        <PortfolioSummary
          spv={spv}
          selectedSummarySPV={selectedSummarySPV}
          setSelectedSummarySPV={setSelectedSummarySPV}
        />
      ) : (
        <Box className={classes.portfolioContainer}>
          <PendingInvestment />
          <Box className={classes.mainHeader}>
            <Typography className={classes.mainHeaderText}>
              Portfolio
            </Typography>
            <Box className={classes.filterBox}>
              <Box className={classes.filterControl}>
                <Select
                  labelId="demo-controlled-open-select-label"
                  id="demo-controlled-open-select"
                  open={open}
                  onClose={handleFilterClose}
                  onOpen={handleFilterOpen}
                  name="Investment"
                  value={selectedSPV}
                  displayEmpty
                  onChange={(event: any) => {
                    setSelectedSPV(event.target.value);
                    setSelectedSummarySPV(event.target.value);
                    history.push(
                      `/portfolio?spv=${get(
                        find(dealList, { id: event.target.value }),
                        'deal',
                      )}`,
                    );
                  }}
                  className={classes.selectBox}
                >
                  <MenuItem value={''}>All</MenuItem>
                  {dealList.map((deal: Deal) =>
                    investorDeals.includes(deal.id) ? (
                      <MenuItem value={get(deal, 'id')} key={get(deal, 'id')}>
                        SPV {get(deal, 'deal')}
                      </MenuItem>
                    ) : null,
                  )}
                </Select>
              </Box>
            </Box>
          </Box>
          <Box className={classes.portfolioBody}>
            <Box className={classes.portfHBox}>
              <Box className={classes.portfoBoxs}>
                <Typography className={classes.pBoxText}>Invested</Typography>
                <Typography className={classes.pBoxHText}>
                  {formatAmount(
                    get(aggregatePortfolioData, 'totalInvestments', 0),
                  )}
                </Typography>
              </Box>
              <Box className={classes.portfoBoxs}>
                <Typography className={classes.pBoxText}>NAV</Typography>
                {/* <Typography className={classes.pBSText}>
                  As of {moment().format('MMM-YY')}
                </Typography> */}
                <Typography className={classes.pBoxHText}>
                  {' '}
                  {formatAmount(
                    get(aggregatePortfolioData, 'totalAssetValue', 0),
                  )}
                </Typography>
              </Box>
              <Box className={classes.portfoBoxs}>
                <Typography className={classes.pBoxText}>
                  Rollover / Distributions
                </Typography>
                <Typography className={classes.pBoxHText}>
                  {formatAmount(
                    get(aggregatePortfolioData, 'comulativeDistribution', 0),
                  )}
                </Typography>
              </Box>
              <Box className={classes.portfoBoxs}>
                <Typography className={classes.pBoxText}>Multiple</Typography>
                <Typography className={classes.pBoxHText}>
                  {parseFloat(
                    get(aggregatePortfolioData, 'multiple') || 0,
                  ).toFixed(1)}{' '}
                  X
                </Typography>
              </Box>
            </Box>
            <Box className={classes.portfoGraph}>
              <Box className={classes.graphHeader}>
                <Switch
                  classes={{
                    root: classes.switchRoot,
                    switchBase: classes.switchBase,
                    thumb: classes.thumb,
                    track: classes.track,
                    checked: classes.checked,
                  }}
                  checked={!selectedNav}
                  onChange={handleSwitchChange}
                  name="areaValueType"
                  inputProps={{ 'aria-label': 'secondary checkbox' }}
                />
                {/* <Box className={classes.filterBox}>
                  <Box className={classes.filterControl}>
                    <Typography variant="caption" className={classes.formLabel}>
                      Deal :
                    </Typography>
                    <Select
                      labelId="demo-controlled-open-select-label"
                      id="demo-controlled-open-select"
                      open={open}
                      onClose={handleFilterClose}
                      onOpen={handleFilterOpen}
                      name="Investment"
                      value={selectedSPV}
                      displayEmpty
                      onChange={(event: any) => {
                        setSelectedSPV(event.target.value);
                      }}
                      className={classes.selectBox}
                    >
                      <MenuItem value={''}>All</MenuItem>
                      {dealList.map((deal: Deal) => (
                        <MenuItem value={get(deal, 'id')} key={get(deal, 'id')}>
                          SPV {get(deal, 'deal')}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box> */}
              </Box>
              <Typography className={classes.graphPHText}>
                <Typography variant="caption" className={classes.gDollorText}>
                  $
                </Typography>
                {!isEmpty(selectedSPV)
                  ? formatMoney(
                      getNavTotal(
                        navTrackData,
                        portfolioCardData,
                        selectedNav,
                      ) || 0,
                    )
                  : formatMoney(
                      (selectedNav
                        ? get(chartData, `${[chartData.length - 1]}.nav`)
                        : get(chartData, `${[chartData.length - 1]}.tv`)) || 0,
                    )}
              </Typography>
              <Typography className={classes.graphPSText}>
                As of{' '}
                {!isEmpty(selectedSPV)
                  ? moment(
                      getNavTotalLatestDate(
                        navTrackData,
                        get(portfolioCardData, 'investmentDate'),
                      ),
                    ).format('DD MMM, YY')
                  : moment().format('DD MMM, YY')}
              </Typography>
              <Box className={classes.chatGraphBox}>
                {!isEmpty(chartData) && (
                  <Chart data={chartData} height={250}>
                    <ArgumentAxis tickFormat={format} />
                    <LineSeries
                      name="NavLine"
                      valueField={selectedNav ? 'nav' : 'tv'}
                      argumentField="month"
                      color="#322EDC"
                      seriesComponent={Line}
                    />
                    <AreaSeries
                      name="NavArea"
                      valueField={selectedNav ? 'nav' : 'tv'}
                      argumentField="month"
                      color="#322EDC"
                      seriesComponent={Area}
                    />
                    <Animation />
                    <EventTracker />
                    <Tooltip
                      targetItem={targetItem}
                      onTargetItemChange={(targetItem: any) =>
                        setTargetItem(targetItem)
                      }
                      contentComponent={TooltipContent}
                    />
                  </Chart>
                )}
              </Box>
            </Box>
            <Box className={classes.portfoTable}>
              <Table stickyHeader aria-label="customized table">
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <StyledTableCell>Investment</StyledTableCell>
                    <StyledTableCell>Status </StyledTableCell>
                    <StyledTableCell>Invest Date</StyledTableCell>
                    <StyledTableCell>Invested </StyledTableCell>
                    <StyledTableCell>Total Value</StyledTableCell>
                    <StyledTableCell>Multiple </StyledTableCell>
                    <StyledTableCell>Actions</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentsList.map((row: any) => (
                    <StyledTableRow key={row.id}>
                      <StyledTableCell>
                        <a
                          className={classes.invstBodyText}
                          onClick={() => {
                            setSelectedSummarySPV(row.spv);
                            history.push(
                              `/portfolio?spv=${get(
                                find(dealList, { id: row?.spv }),
                                'deal',
                              )}`,
                            );
                          }}
                        >
                          SPV {get(find(dealList, { id: row?.spv }), 'deal')}
                        </a>
                      </StyledTableCell>
                      {!row.approvedByAdmin ? (
                        <StyledTableCell>
                          <a
                            className={classes.confirmPendingText}
                            onClick={() => history.push('/invest-now')}
                          >
                            {'Confirmation pending'}
                          </a>
                        </StyledTableCell>
                      ) : (
                        <StyledTableCell>
                          <span style={{ color: 'green' }}>{'Confirmed'}</span>
                        </StyledTableCell>
                      )}
                      <StyledTableCell>
                        {' '}
                        {moment(
                          get(row, 'investmentDetail.investmentDate'),
                        ).format('DD-MM-YYYY')}
                      </StyledTableCell>
                      <StyledTableCell>
                        {' '}
                        {formatAmount(get(row, 'amount', 0))}
                      </StyledTableCell>
                      <StyledTableCell>
                        {' '}
                        {formatAmount(
                          parseFloat(get(row, 'portfolio.totalAssetValue', 0)) +
                            parseFloat(
                              get(row, 'portfolio.comulativeDistribution', 0),
                            ),
                        )}
                      </StyledTableCell>
                      <StyledTableCell>
                        {' '}
                        {parseFloat(
                          get(row, 'portfolio.multiple') || 0,
                        ).toFixed(1)}{' '}
                        X
                      </StyledTableCell>
                      <StyledTableCell>
                        -
                        {/* <Button
                          name="Actions"
                          className={classes.portfoABtn}
                          disabled
                        /> */}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                  <StyledTableRow>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.emptyRowStyle}
                    ></StyledTableCell>
                  </StyledTableRow>
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};
export default Portfolio;
